"use client";

import { Button } from "@/components/ui/Button";
import { DialogHeader } from "@/components/ui/Dialog";
import {
  Drawer,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/Drawer";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/Form";
import { P } from "@/components/ui/Heading";
import { Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { useSearch } from "@/contexts/SearchContext";
import { SearchAlertDuration } from "@/enums/search-alert.enum";
import {
  useCreateSearchAlert,
  useEditSearchAlert,
} from "@/hooks/useSearchAlert";
import { SavedSearchAlert } from "@/interfaces/search-alert.interface";
import { gTagEvents } from "@/lib/gtag";
import { convertStateToPropertyFilters } from "@/lib/property/listing/helpers";
import { Dialog, DialogContent, DialogTitle } from "@components/ui/Dialog";
import { useTranslations } from "next-intl";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { useWindowSize } from "usehooks-ts";

const SearchAlertModalContent = ({
  title,
  isEdit = false,
  alert,
  onClose,
}: {
  title: string;
  isEdit: boolean;
  alert?: SavedSearchAlert;
  onClose: () => void;
}) => {
  const t = useTranslations("search-alert.form");
  const { state } = useSearch();
  const createSearchAlert = useCreateSearchAlert();
  const editSearchAlert = useEditSearchAlert(alert?.id ?? ""); // passing empty for create

  const form = useForm({
    values: {
      name: title,
      duration: SearchAlertDuration.DAILY,
    },
  });

  const handleSubmit = (data: {
    name: string;
    duration: SearchAlertDuration;
  }) => {
    const filters = convertStateToPropertyFilters(state);
    if (isEdit) {
      editSearchAlert.mutate(
        {
          duration: data.duration,
          name: data.name,
        },
        {
          onSuccess() {
            toast.success(t("edit.state.success"));
            onClose();
          },
          onError() {
            toast.error(t("edit.state.error"));
          },
        }
      );
    } else {
      createSearchAlert.mutate(
        {
          duration: data.duration,
          filters: filters,
          name: data.name,
        },
        {
          onSuccess(data) {
            gTagEvents.propertySearchCreated(state, data);
            toast.success(t("create.state.success"));
            onClose();
          },
          onError() {
            toast.error(t("create.state.error"));
          },
        }
      );
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <P>{isEdit ? t("edit.description") : t("create.description")}</P>
      <Form {...form}>
        <form
          className="grid grid-cols-1 gap-4 sm:gap-4 w-full max-w-4xl"
          onSubmit={form.handleSubmit(handleSubmit)}
        >
          <FormField
            name="name"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t("fields.name.title")}</FormLabel>
                <FormControl>
                  <Input
                    {...field}
                    value={field.value}
                    placeholder={t("fields.name.placeholder")}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="duration"
            render={({ field }) => (
              <FormItem className="space-y-2">
                <FormLabel>{t("fields.duration.title")}</FormLabel>
                <FormControl>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <SelectTrigger className="!h-fit w-full">
                      <SelectValue
                        placeholder={t("fields.duration.placeholder")}
                      />
                    </SelectTrigger>

                    <SelectContent>
                      {Object.values(SearchAlertDuration).map((value, i) => (
                        <SelectItem value={value} key={i}>
                          {t(`fields.duration.options.${value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <Button
            size={"lg"}
            loading={createSearchAlert.isPending || editSearchAlert.isPending}
          >
            {isEdit ? t("edit.cta") : t("create.cta")}
          </Button>
        </form>
      </Form>
    </div>
  );
};

export const CreateEditSearchAlertModal = ({
  isEdit = false,
  isOpen,
  onClose,
  title,
  alert,
}: {
  isEdit?: boolean;
  isOpen: boolean;
  onClose: () => void;
  title: string;
  alert?: SavedSearchAlert;
}) => {
  const { width } = useWindowSize();
  const t = useTranslations("search-alert.form");
  const isMobile = width < 768;

  if (isMobile) {
    return (
      <Drawer open={isOpen} onOpenChange={onClose}>
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle className="sr-only cursor-no">
              {isEdit ? t("edit.title") : t("create.title")}
            </DrawerTitle>
          </DrawerHeader>
          <div className=" relative px-4">
            <SearchAlertModalContent
              onClose={onClose}
              title={title}
              isEdit={isEdit}
              alert={alert}
            />
          </div>
          <DrawerFooter className="!py-0.5" />
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl px-5">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? t("edit.title") : t("create.title")}
          </DialogTitle>
        </DialogHeader>
        <SearchAlertModalContent
          title={title}
          isEdit={isEdit}
          alert={alert}
          onClose={onClose}
        />
      </DialogContent>
    </Dialog>
  );
};
